import {
  X,
  User,
  Phone,
  ShoppingCart,
  DollarSign,
  Clock,
  FileText,
  Sparkles,
  ArrowRight,
  Receipt,
  Palette,
  Building2,
  Printer,
  Upload,
  Link2,
  Trash2,
} from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"
import { ChangeEvent, ReactNode, useEffect, useMemo, useState } from "react"
import { getStoreExperience } from "../../utils/storeExperience"
import { isPropertyStore } from "../../utils/storeCategory"

type Props = {
  clientData: any | null
  store?: any
  onClose: () => void
  onOpenSale: (sale: any) => void
}

type InvoiceTemplate = "classic" | "modern" | "executive"

type InvoiceBranding = {
  storeName: string
  logoUrl: string
  phone: string
  email: string
  city: string
  address: string
  footerNote: string
}

const TEMPLATE_OPTIONS: Array<{ value: InvoiceTemplate; label: string; hint: string }> = [
  { value: "classic", label: "Classique", hint: "sobre, lisible, facture standard" },
  { value: "modern", label: "Moderne", hint: "plus visuel, plus premium" },
  { value: "executive", label: "Executive", hint: "plus dense, esprit rapport client" },
]

export default function ClientDetailsModal({
  clientData,
  store,
  onClose,
  onOpenSale,
}: Props) {
  const propertyStore = isPropertyStore(store)
  const experience = getStoreExperience(store)
  const [invoiceStudioOpen, setInvoiceStudioOpen] = useState(false)
  const [template, setTemplate] = useState<InvoiceTemplate>("classic")
  const [brandingReady, setBrandingReady] = useState(false)
  const [branding, setBranding] = useState<InvoiceBranding>({
    storeName: "",
    logoUrl: "",
    phone: "",
    email: "",
    city: "",
    address: "",
    footerNote: "",
  })

  const storageKey = useMemo(() => `gestock-client-invoice-branding:${String(store?._id || "default")}`, [store?._id])

  useEffect(() => {
    if (!clientData) return

    const savedRaw = window.localStorage.getItem(storageKey)
    const saved = savedRaw ? safeParseBranding(savedRaw) : null
    setBranding({
      storeName: saved?.storeName || store?.name || "Votre boutique",
      logoUrl: saved?.logoUrl || "",
      phone: saved?.phone || store?.phone || "",
      email: saved?.email || store?.email || "",
      city: saved?.city || store?.city || "",
      address: saved?.address || store?.address || "",
      footerNote:
        saved?.footerNote ||
        "Merci pour votre confiance. Cette facture globale reprend l'ensemble des operations du client.",
    })
    setBrandingReady(true)
  }, [clientData, storageKey, store?.address, store?.city, store?.email, store?.name, store?.phone])

  useEffect(() => {
    if (!brandingReady) return
    window.localStorage.setItem(storageKey, JSON.stringify(branding))
  }, [branding, brandingReady, storageKey])

  if (!clientData) return null

  const { client, summary, sales } = clientData
  const sortedSales = [...sales].sort(
    (a: any, b: any) => new Date(b?.soldAt || 0).getTime() - new Date(a?.soldAt || 0).getTime()
  )
  const totalSalesCount = Number(summary?.totalSales || sortedSales.length || 0)
  const invoiceReference = buildInvoiceReference(store?._id, client?.phone || client?.clientPhone)

  const invoiceTotals = sortedSales.reduce(
    (acc, sale: any) => {
      acc.total += Number(sale?.sellPrice || 0)
      acc.paid += Number(sale?.paid || 0)
      acc.remaining += Number(sale?.remaining || 0)
      return acc
    },
    { total: 0, paid: 0, remaining: 0 }
  )

  const heroClass = propertyStore
    ? "from-emerald-900 via-teal-700 to-cyan-700"
    : "from-sky-700 via-blue-700 to-indigo-900"
  const cardTheme = propertyStore ? "border-emerald-100 bg-emerald-50/50" : "border-slate-200 bg-white"
  const accentButtonClass = propertyStore ? "bg-emerald-600 hover:bg-emerald-700" : "bg-blue-600 hover:bg-blue-700"

  const handlePrintGlobalInvoice = () => {
    const invoiceHtml = buildInvoiceHtml({
      template,
      branding,
      client,
      store,
      propertyStore,
      summary,
      sales: sortedSales,
      totals: invoiceTotals,
      reference: invoiceReference,
    })

    const printWindow = window.open("", "_blank", "width=1100,height=900")
    if (!printWindow) return
    printWindow.document.open()
    printWindow.document.write(invoiceHtml)
    printWindow.document.close()
    printWindow.focus()
  }

  const handleLogoFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith("image/")) {
      event.target.value = ""
      return
    }

    const reader = new FileReader()
    reader.onload = () => {
      const result = typeof reader.result === "string" ? reader.result : ""
      setBranding((prev) => ({ ...prev, logoUrl: result }))
      event.target.value = ""
    }
    reader.readAsDataURL(file)
  }

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <div className="flex h-full items-center justify-center p-3 sm:p-4">
          <motion.div
            className="relative flex h-[94vh] w-full max-w-7xl overflow-hidden rounded-[2rem] bg-white shadow-2xl"
            initial={{ scale: 0.96, y: 28 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.96, y: 24 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
          >
            <div className="flex min-w-0 flex-1 flex-col">
              <div className={`relative overflow-hidden border-b border-white/10 bg-gradient-to-r px-6 py-5 text-white ${heroClass}`}>
                <div className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-white/15 blur-3xl" />
                <div className="pointer-events-none absolute -bottom-12 left-10 h-28 w-28 rounded-full bg-cyan-200/15 blur-3xl" />

                <div className="relative flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div className="flex items-start gap-4">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 backdrop-blur-sm">
                      <User size={24} />
                    </div>
                    <div>
                      <p className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-1 text-[11px] uppercase tracking-[0.16em]">
                        <Sparkles size={12} /> {propertyStore ? experience.badge : "Client Desk"}
                      </p>
                      <h2 className="mt-3 text-2xl font-bold">{client.name || client.clientName}</h2>
                      <p className="mt-1 flex items-center gap-2 text-sm text-white/90">
                        <Phone size={14} /> {client.phone || client.clientPhone || "-"}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setInvoiceStudioOpen((current) => !current)}
                      className="inline-flex items-center gap-2 rounded-2xl border border-white/20 bg-white/10 px-4 py-2.5 text-sm font-medium text-white backdrop-blur-sm transition hover:bg-white/15"
                    >
                      <Receipt size={16} />
                      Facture globale
                    </button>
                    <button
                      onClick={onClose}
                      className="inline-flex items-center gap-2 rounded-2xl border border-white/20 bg-white/10 px-4 py-2.5 text-sm font-medium text-white backdrop-blur-sm transition hover:bg-white/15"
                    >
                      <X size={16} />
                      Fermer
                    </button>
                  </div>
                </div>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto bg-slate-50">
                <div className="space-y-6 p-5 sm:p-6">
                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
                    <KpiCard
                      label={propertyStore ? "Dossiers" : "Achats"}
                      value={totalSalesCount}
                      icon={<ShoppingCart size={18} />}
                      gradient={propertyStore ? "from-emerald-600 to-cyan-500" : "from-blue-600 to-cyan-500"}
                    />
                    <KpiCard
                      label="Montant total"
                      value={`${invoiceTotals.total.toLocaleString()} FCFA`}
                      icon={<FileText size={18} />}
                      gradient="from-slate-700 to-slate-500"
                    />
                    <KpiCard
                      label="Total payé"
                      value={`${Number(summary.totalPaid || 0).toLocaleString()} FCFA`}
                      icon={<DollarSign size={18} />}
                      gradient="from-emerald-600 to-green-500"
                    />
                    <KpiCard
                      label="Reste à payer"
                      value={`${Number(summary.totalDebt || 0).toLocaleString()} FCFA`}
                      icon={<Clock size={18} />}
                      gradient="from-rose-600 to-orange-500"
                    />
                  </div>

                  <section className={`rounded-3xl border p-5 shadow-sm ${cardTheme}`}>
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                      <div>
                        <p className="text-sm font-semibold text-slate-800">Profil client</p>
                        <p className="mt-1 text-sm text-slate-500">
                          Vue d'ensemble, historique complet et point d'entrée pour la facture globale.
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Badge
                          tone={Number(summary.totalDebt || 0) > 0 ? "danger" : "success"}
                          label={Number(summary.totalDebt || 0) > 0 ? "Dette en cours" : "Compte à jour"}
                        />
                        <Badge tone="neutral" label={`Réf. ${invoiceReference}`} />
                      </div>
                    </div>

                    <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-3">
                      <InfoTile icon={<User size={16} />} label="Nom du client" value={client.name || client.clientName || "-"} />
                      <InfoTile icon={<Phone size={16} />} label="Téléphone" value={client.phone || client.clientPhone || "-"} />
                      <InfoTile
                        icon={<Receipt size={16} />}
                        label={propertyStore ? "Operations" : "Ventes"}
                        value={`${sortedSales.length}`}
                      />
                    </div>
                  </section>

                  <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
                    <div className="border-b border-slate-100 bg-gradient-to-r from-white to-slate-50 px-5 py-4">
                      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                        <div>
                          <p className="text-sm font-semibold text-slate-800">Historique détaillé</p>
                          <p className="mt-1 text-xs text-slate-500">
                            Ouvrez chaque opération pour sa facture individuelle ou utilisez la facture globale à droite.
                          </p>
                        </div>
                        <button
                          onClick={() => setInvoiceStudioOpen(true)}
                          className={`inline-flex items-center gap-2 rounded-2xl px-4 py-2.5 text-sm font-medium text-white transition ${accentButtonClass}`}
                        >
                          Préparer la facture globale
                          <ArrowRight size={15} />
                        </button>
                      </div>
                    </div>

                    <div className="hidden md:block">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50">
                          <tr className="text-left text-slate-500">
                            <th className="px-5 py-3">Date</th>
                            <th className="px-5 py-3">{propertyStore ? "Bien" : "Produit"}</th>
                            {propertyStore && <th className="px-5 py-3">Type</th>}
                            {propertyStore && <th className="px-5 py-3">Periode</th>}
                            <th className="px-5 py-3">Montant</th>
                            <th className="px-5 py-3">Payé</th>
                            <th className="px-5 py-3">Reste</th>
                            <th className="px-5 py-3 text-right">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedSales.map((sale: any) => (
                            <tr key={sale._id} className="border-t border-slate-100 transition hover:bg-slate-50/80">
                              <td className="px-5 py-4 text-slate-600">{formatDate(sale.soldAt)}</td>
                              <td className="px-5 py-4">
                                <div>
                                  <p className="font-medium text-slate-800">{resolveProductName(sale)}</p>
                                  <p className="text-xs text-slate-500">{resolveProductReference(sale)}</p>
                                </div>
                              </td>
                              {propertyStore && (
                                <td className="px-5 py-4">
                                  <Badge tone={resolveTransactionType(sale) === "Location" ? "warning" : "success"} label={resolveTransactionType(sale)} />
                                </td>
                              )}
                              {propertyStore && <td className="px-5 py-4 text-slate-600">{resolveRentalPeriod(sale)}</td>}
                              <td className="px-5 py-4 font-medium text-slate-800">{Number(sale.sellPrice || 0).toLocaleString()} FCFA</td>
                              <td className="px-5 py-4 font-medium text-emerald-700">{Number(sale.paid || 0).toLocaleString()} FCFA</td>
                              <td className="px-5 py-4">
                                <span className={Number(sale.remaining || 0) > 0 ? "font-semibold text-rose-700" : "text-emerald-700"}>
                                  {Number(sale.remaining || 0).toLocaleString()} FCFA
                                </span>
                              </td>
                              <td className="px-5 py-4 text-right">
                                <button
                                  onClick={() => onOpenSale(sale)}
                                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-slate-700 transition hover:bg-slate-100"
                                >
                                  <FileText size={14} />
                                  Facture
                                </button>
                              </td>
                            </tr>
                          ))}

                          {sortedSales.length === 0 && (
                            <tr>
                              <td colSpan={propertyStore ? 8 : 6} className="py-12 text-center text-slate-500">
                                {propertyStore ? "Aucun historique immobilier pour ce contact" : "Aucun historique pour ce client"}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    <div className="space-y-3 p-4 md:hidden">
                      {sortedSales.map((sale: any) => (
                        <div key={sale._id} className="rounded-2xl border border-slate-200 bg-white p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="font-semibold text-slate-800">{resolveProductName(sale)}</p>
                              <p className="text-xs text-slate-500">{formatDate(sale.soldAt)}</p>
                            </div>
                            <Badge
                              tone={Number(sale.remaining || 0) > 0 ? "danger" : "success"}
                              label={Number(sale.remaining || 0) > 0 ? "Reste" : "Réglé"}
                            />
                          </div>

                          <div className="mt-3 space-y-2 text-sm">
                            <MobileRow label="Montant" value={`${Number(sale.sellPrice || 0).toLocaleString()} FCFA`} />
                            <MobileRow label="Payé" value={`${Number(sale.paid || 0).toLocaleString()} FCFA`} />
                            <MobileRow label="Reste" value={`${Number(sale.remaining || 0).toLocaleString()} FCFA`} />
                          </div>

                          <button
                            onClick={() => onOpenSale(sale)}
                            className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 py-2.5 text-slate-700 transition hover:bg-slate-50"
                          >
                            Voir la facture
                            <ArrowRight size={14} />
                          </button>
                        </div>
                      ))}

                      {sortedSales.length === 0 && <div className="py-10 text-center text-slate-500">Aucun historique pour ce client</div>}
                    </div>
                  </section>
                </div>
              </div>
            </div>

            <AnimatePresence initial={false}>
              {invoiceStudioOpen && (
                <motion.aside
                  className="absolute inset-0 z-10 flex w-full flex-col border-l border-slate-200 bg-white lg:relative lg:w-[410px] lg:shrink-0"
                  initial={{ x: 32, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: 24, opacity: 0 }}
                >
                  <div className="border-b border-slate-100 px-5 py-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-slate-800">Studio facture globale</p>
                        <p className="mt-1 text-xs text-slate-500">
                          Personnalisez l’entête, le logo et le style avant impression.
                        </p>
                      </div>
                      <button onClick={() => setInvoiceStudioOpen(false)} className="rounded-xl p-2 text-slate-500 transition hover:bg-slate-100">
                        <X size={16} />
                      </button>
                    </div>
                  </div>

                  <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-5">
                    <div className="space-y-3">
                      <p className="inline-flex items-center gap-2 text-sm font-semibold text-slate-800">
                        <Palette size={16} />
                        Templates
                      </p>
                      <div className="grid gap-2">
                        {TEMPLATE_OPTIONS.map((option) => (
                          <button
                            key={option.value}
                            onClick={() => setTemplate(option.value)}
                            className={`rounded-2xl border px-4 py-3 text-left transition ${template === option.value ? "border-blue-200 bg-blue-50" : "border-slate-200 hover:bg-slate-50"}`}
                          >
                            <p className="text-sm font-semibold text-slate-800">{option.label}</p>
                            <p className="mt-1 text-xs text-slate-500">{option.hint}</p>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-3">
                      <p className="inline-flex items-center gap-2 text-sm font-semibold text-slate-800">
                        <Building2 size={16} />
                        Identité de facture
                      </p>
                      <StudioField label="Nom de la boutique">
                        <input
                          value={branding.storeName}
                          onChange={(e) => setBranding((prev) => ({ ...prev, storeName: e.target.value }))}
                          className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-4 focus:ring-blue-100"
                        />
                      </StudioField>
                      <StudioField label="Logo URL">
                        <div className="mt-1 space-y-3">
                          <div className="flex items-center gap-2 rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-3">
                            <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-100">
                              <Upload size={15} />
                              Importer depuis l'appareil
                              <input type="file" accept="image/*" className="hidden" onChange={handleLogoFileChange} />
                            </label>
                            {branding.logoUrl ? (
                              <button
                                type="button"
                                onClick={() => setBranding((prev) => ({ ...prev, logoUrl: "" }))}
                                className="inline-flex items-center gap-1 rounded-xl px-3 py-2 text-sm text-rose-600 transition hover:bg-rose-50"
                              >
                                <Trash2 size={14} />
                                Retirer
                              </button>
                            ) : (
                              <span className="text-xs text-slate-500">PNG, JPG ou WebP</span>
                            )}
                          </div>

                          <div className="relative">
                            <Link2 size={15} className="pointer-events-none absolute left-3 top-3 text-slate-400" />
                            <input
                              value={branding.logoUrl}
                              onChange={(e) => setBranding((prev) => ({ ...prev, logoUrl: e.target.value }))}
                              placeholder="ou collez une URL https://..."
                              className="w-full rounded-xl border border-slate-200 py-2.5 pl-9 pr-3 text-sm outline-none focus:ring-4 focus:ring-blue-100"
                            />
                          </div>

                          {branding.logoUrl && (
                            <div className="rounded-2xl border border-slate-200 bg-white p-3">
                              <p className="mb-2 text-xs font-medium uppercase tracking-[0.12em] text-slate-400">
                                Aperçu logo
                              </p>
                              <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-2xl bg-slate-100">
                                <img src={branding.logoUrl} alt="Logo facture" className="h-full w-full object-cover" />
                              </div>
                            </div>
                          )}
                        </div>
                      </StudioField>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <StudioField label="Téléphone">
                          <input
                            value={branding.phone}
                            onChange={(e) => setBranding((prev) => ({ ...prev, phone: e.target.value }))}
                            className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-4 focus:ring-blue-100"
                          />
                        </StudioField>
                        <StudioField label="Email">
                          <input
                            value={branding.email}
                            onChange={(e) => setBranding((prev) => ({ ...prev, email: e.target.value }))}
                            className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-4 focus:ring-blue-100"
                          />
                        </StudioField>
                      </div>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <StudioField label="Ville">
                          <input
                            value={branding.city}
                            onChange={(e) => setBranding((prev) => ({ ...prev, city: e.target.value }))}
                            className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-4 focus:ring-blue-100"
                          />
                        </StudioField>
                        <StudioField label="Adresse">
                          <input
                            value={branding.address}
                            onChange={(e) => setBranding((prev) => ({ ...prev, address: e.target.value }))}
                            className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-4 focus:ring-blue-100"
                          />
                        </StudioField>
                      </div>
                      <StudioField label="Note de bas de facture">
                        <textarea
                          rows={3}
                          value={branding.footerNote}
                          onChange={(e) => setBranding((prev) => ({ ...prev, footerNote: e.target.value }))}
                          className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-4 focus:ring-blue-100"
                        />
                      </StudioField>
                    </div>

                    <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-sm font-semibold text-slate-800">Aperçu rapide</p>
                      <div className="mt-3 space-y-2 text-sm">
                        <PreviewRow icon={<User size={14} />} label="Client" value={client.name || client.clientName || "-"} />
                        <PreviewRow icon={<Phone size={14} />} label="Téléphone" value={client.phone || client.clientPhone || "-"} />
                        <PreviewRow icon={<FileText size={14} />} label="Référence" value={invoiceReference} />
                        <PreviewRow icon={<DollarSign size={14} />} label="Montant" value={`${invoiceTotals.total.toLocaleString()} FCFA`} />
                        <PreviewRow icon={<Clock size={14} />} label="Reste" value={`${invoiceTotals.remaining.toLocaleString()} FCFA`} />
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-slate-100 px-5 py-4">
                    <button
                      onClick={handlePrintGlobalInvoice}
                      className={`inline-flex w-full items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm font-semibold text-white transition ${accentButtonClass}`}
                    >
                      <Printer size={16} />
                      Imprimer la facture globale
                    </button>
                  </div>
                </motion.aside>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </motion.div>
    </AnimatePresence>
  )
}

function KpiCard({
  label,
  value,
  icon,
  gradient,
}: {
  label: string
  value: string | number
  icon: ReactNode
  gradient: string
}) {
  return (
    <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
      <div className={`flex items-center justify-between bg-gradient-to-r px-4 py-4 text-white ${gradient}`}>
        <div className="rounded-2xl bg-white/15 p-2.5">{icon}</div>
        <Sparkles size={16} className="text-white/80" />
      </div>
      <div className="px-4 py-4">
        <p className="text-xs uppercase tracking-[0.12em] text-slate-500">{label}</p>
        <p className="mt-2 text-xl font-semibold text-slate-800">{value}</p>
      </div>
    </div>
  )
}

function Badge({ tone, label }: { tone: "danger" | "success" | "warning" | "neutral"; label: string }) {
  const toneClass =
    tone === "danger"
      ? "bg-rose-100 text-rose-700"
      : tone === "success"
        ? "bg-emerald-100 text-emerald-700"
        : tone === "warning"
          ? "bg-amber-100 text-amber-700"
          : "bg-slate-100 text-slate-700"

  return <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${toneClass}`}>{label}</span>
}

function InfoTile({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.12em] text-slate-400">
        {icon}
        {label}
      </div>
      <p className="mt-3 text-sm font-semibold text-slate-800">{value}</p>
    </div>
  )
}

function MobileRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-slate-800">{value}</span>
    </div>
  )
}

function StudioField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      {children}
    </label>
  )
}

function PreviewRow({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="inline-flex items-center gap-2 text-slate-500">
        {icon}
        <span>{label}</span>
      </div>
      <span className="text-right font-medium text-slate-800">{value}</span>
    </div>
  )
}

function resolveProductName(sale: any) {
  return sale?.modelId?.name || sale?.productName || sale?.productId?.name || "Produit"
}

function resolveProductReference(sale: any) {
  return sale?.identifier || sale?.productId?.identifier || sale?.imei || "Référence indisponible"
}

function resolveTransactionType(sale: any) {
  const type = String(sale?.transactionType || sale?.listingType || "SALE").toUpperCase()
  if (type === "RENT") return "Location"
  if (type === "BOTH") return "Vente & location"
  return "Vente"
}

function resolveRentalPeriod(sale: any) {
  const start = sale?.rentalStartDate || sale?.rentStartDate || sale?.startDate
  const end = sale?.rentalEndDate || sale?.rentEndDate || sale?.endDate

  if (!start && !end) return "—"
  return `${start ? formatDate(start) : "—"} -> ${end ? formatDate(end) : "—"}`
}

function formatDate(value: string) {
  return new Date(value).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  })
}

function buildInvoiceReference(storeId?: string, phone?: string) {
  const safeStore = String(storeId || "STORE").slice(-4).toUpperCase()
  const safePhone = String(phone || "0000").replace(/[^\d]/g, "").slice(-4) || "0000"
  return `CLT-${safeStore}-${safePhone}`
}

function safeParseBranding(raw: string): InvoiceBranding | null {
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

function buildInvoiceHtml({
  template,
  branding,
  client,
  propertyStore,
  summary,
  sales,
  totals,
  reference,
}: {
  template: InvoiceTemplate
  branding: InvoiceBranding
  client: any
  store?: any
  propertyStore: boolean
  summary: any
  sales: any[]
  totals: { total: number; paid: number; remaining: number }
  reference: string
}) {
  const palette =
    template === "modern"
      ? {
          hero: "#0f172a",
          accent: "#2563eb",
          soft: "#eff6ff",
          ink: "#0f172a",
        }
      : template === "executive"
        ? {
            hero: "#111827",
            accent: "#059669",
            soft: "#ecfdf5",
            ink: "#111827",
          }
        : {
            hero: "#1e3a8a",
            accent: "#0ea5e9",
            soft: "#f8fafc",
            ink: "#0f172a",
          }

  const lineItems = sales
    .map((sale, index) => {
      const transaction = propertyStore ? resolveTransactionType(sale) : "Vente"
      const period = propertyStore ? resolveRentalPeriod(sale) : "—"
      return `
        <tr>
          <td>${index + 1}</td>
          <td>
            <strong>${escapeHtml(resolveProductName(sale))}</strong><br />
            <span class="muted">${escapeHtml(resolveProductReference(sale))}</span>
          </td>
          <td>${escapeHtml(transaction)}</td>
          <td>${escapeHtml(period)}</td>
          <td>${formatDate(sale.soldAt)}</td>
          <td class="num">${Number(sale.sellPrice || 0).toLocaleString()} FCFA</td>
          <td class="num">${Number(sale.paid || 0).toLocaleString()} FCFA</td>
          <td class="num">${Number(sale.remaining || 0).toLocaleString()} FCFA</td>
        </tr>
      `
    })
    .join("")

  return `
    <!DOCTYPE html>
    <html lang="fr">
      <head>
        <meta charset="UTF-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <title>Facture globale ${escapeHtml(reference)}</title>
        <style>
          * { box-sizing: border-box; }
          body {
            margin: 0;
            font-family: Inter, Arial, sans-serif;
            color: ${palette.ink};
            background: #eef2ff;
          }
          .page {
            width: 100%;
            max-width: 1040px;
            margin: 24px auto;
            background: #fff;
            border-radius: 28px;
            overflow: hidden;
            box-shadow: 0 24px 60px rgba(15, 23, 42, 0.12);
          }
          .hero {
            padding: 32px 36px;
            background: linear-gradient(135deg, ${palette.hero}, ${palette.accent});
            color: white;
          }
          .hero-top {
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            gap: 20px;
          }
          .brand {
            display: flex;
            align-items: center;
            gap: 16px;
          }
          .logo {
            width: 68px;
            height: 68px;
            border-radius: 20px;
            background: rgba(255,255,255,0.16);
            overflow: hidden;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 26px;
            font-weight: 700;
          }
          .logo img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
          .hero small {
            display: inline-block;
            padding: 6px 10px;
            border-radius: 999px;
            background: rgba(255,255,255,0.16);
            letter-spacing: .12em;
            text-transform: uppercase;
            font-size: 11px;
          }
          .hero h1 {
            margin: 14px 0 6px;
            font-size: 30px;
          }
          .hero p {
            margin: 0;
            color: rgba(255,255,255,0.88);
          }
          .meta {
            text-align: right;
            min-width: 220px;
          }
          .content {
            padding: 28px 36px 36px;
          }
          .grid {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 16px;
          }
          .card {
            border: 1px solid #e2e8f0;
            border-radius: 20px;
            padding: 18px;
            background: ${palette.soft};
          }
          .card h3 {
            margin: 0 0 10px;
            font-size: 13px;
            text-transform: uppercase;
            letter-spacing: .12em;
            color: #64748b;
          }
          .card p {
            margin: 6px 0;
            font-size: 14px;
          }
          .stats {
            display: grid;
            grid-template-columns: repeat(4, minmax(0, 1fr));
            gap: 12px;
            margin-top: 18px;
          }
          .stat {
            border-radius: 18px;
            padding: 16px;
            background: white;
            border: 1px solid #e2e8f0;
          }
          .stat .label {
            font-size: 12px;
            color: #64748b;
            text-transform: uppercase;
            letter-spacing: .12em;
          }
          .stat .value {
            margin-top: 10px;
            font-size: 20px;
            font-weight: 700;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 22px;
            font-size: 14px;
          }
          th, td {
            padding: 14px 10px;
            border-bottom: 1px solid #e2e8f0;
            vertical-align: top;
          }
          th {
            text-align: left;
            color: #64748b;
            font-size: 12px;
            text-transform: uppercase;
            letter-spacing: .1em;
          }
          .num {
            text-align: right;
            white-space: nowrap;
          }
          .muted {
            color: #64748b;
            font-size: 12px;
          }
          .totals {
            margin-top: 22px;
            margin-left: auto;
            width: 100%;
            max-width: 360px;
            border: 1px solid #e2e8f0;
            border-radius: 20px;
            overflow: hidden;
          }
          .total-row {
            display: flex;
            justify-content: space-between;
            gap: 12px;
            padding: 14px 18px;
            border-bottom: 1px solid #e2e8f0;
          }
          .total-row:last-child {
            border-bottom: 0;
            background: ${palette.soft};
            font-weight: 700;
          }
          .footer {
            margin-top: 24px;
            padding-top: 20px;
            border-top: 1px dashed #cbd5e1;
            color: #475569;
            font-size: 13px;
          }
          .actions {
            padding: 0 36px 28px;
          }
          .actions button {
            border: 0;
            border-radius: 14px;
            padding: 12px 18px;
            background: ${palette.hero};
            color: white;
            font-weight: 600;
            cursor: pointer;
          }
          @media print {
            body { background: white; }
            .page { margin: 0; max-width: none; border-radius: 0; box-shadow: none; }
            .actions { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="page">
          <div class="hero">
            <div class="hero-top">
              <div class="brand">
                <div class="logo">
                  ${branding.logoUrl ? `<img src="${escapeAttribute(branding.logoUrl)}" alt="logo" />` : escapeHtml(getLogoFallback(branding.storeName))}
                </div>
                <div>
                  <small>${template === "executive" ? "Client Statement" : "Facture globale"}</small>
                  <h1>${escapeHtml(branding.storeName || "Votre boutique")}</h1>
                  <p>${escapeHtml(branding.address || branding.city || "Coordonnées boutique à personnaliser")}</p>
                </div>
              </div>
              <div class="meta">
                <p><strong>Référence:</strong> ${escapeHtml(reference)}</p>
                <p><strong>Date:</strong> ${formatDate(new Date().toISOString())}</p>
                <p><strong>Client:</strong> ${escapeHtml(client?.name || client?.clientName || "-")}</p>
                <p><strong>Téléphone:</strong> ${escapeHtml(client?.phone || client?.clientPhone || "-")}</p>
              </div>
            </div>
          </div>

          <div class="content">
            <div class="grid">
              <div class="card">
                <h3>Boutique</h3>
                <p>${escapeHtml(branding.storeName || "Votre boutique")}</p>
                <p>${escapeHtml(branding.phone || "-")}</p>
                <p>${escapeHtml(branding.email || "-")}</p>
                <p>${escapeHtml(branding.city || "-")}</p>
                <p>${escapeHtml(branding.address || "-")}</p>
              </div>
              <div class="card">
                <h3>Client</h3>
                <p>${escapeHtml(client?.name || client?.clientName || "-")}</p>
                <p>${escapeHtml(client?.phone || client?.clientPhone || "-")}</p>
                <p>Total opérations: ${Number(summary?.totalSales || sales.length || 0).toLocaleString()}</p>
                <p>Dette restante: ${Number(summary?.totalDebt || 0).toLocaleString()} FCFA</p>
              </div>
            </div>

            <div class="stats">
              <div class="stat">
                <div class="label">Montant total</div>
                <div class="value">${totals.total.toLocaleString()} FCFA</div>
              </div>
              <div class="stat">
                <div class="label">Déjà payé</div>
                <div class="value">${totals.paid.toLocaleString()} FCFA</div>
              </div>
              <div class="stat">
                <div class="label">Reste</div>
                <div class="value">${totals.remaining.toLocaleString()} FCFA</div>
              </div>
              <div class="stat">
                <div class="label">Lignes</div>
                <div class="value">${sales.length.toLocaleString()}</div>
              </div>
            </div>

            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>${propertyStore ? "Bien / produit" : "Produit"}</th>
                  <th>Type</th>
                  <th>Période</th>
                  <th>Date</th>
                  <th class="num">Montant</th>
                  <th class="num">Payé</th>
                  <th class="num">Reste</th>
                </tr>
              </thead>
              <tbody>
                ${lineItems || `<tr><td colspan="8" style="text-align:center;padding:28px 10px;color:#64748b;">Aucune ligne à facturer</td></tr>`}
              </tbody>
            </table>

            <div class="totals">
              <div class="total-row"><span>Total facturé</span><strong>${totals.total.toLocaleString()} FCFA</strong></div>
              <div class="total-row"><span>Total payé</span><strong>${totals.paid.toLocaleString()} FCFA</strong></div>
              <div class="total-row"><span>Reste à payer</span><strong>${totals.remaining.toLocaleString()} FCFA</strong></div>
            </div>

            <div class="footer">
              ${escapeHtml(branding.footerNote || "")}
            </div>
          </div>

          <div class="actions">
            <button onclick="window.print()">Imprimer</button>
          </div>
        </div>
      </body>
    </html>
  `
}

function getLogoFallback(storeName: string) {
  return String(storeName || "ST")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "ST"
}

function escapeHtml(value: string) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;")
}

function escapeAttribute(value: string) {
  return escapeHtml(value)
}
